import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import CategoryLanding from "./pages/CategoryLanding";
import Listings from "./pages/Listings";
import ListingDetail from "./pages/ListingDetail";
import CreateListing from "./pages/CreateListing";
import Dashboard from "./pages/Dashboard";
import Favorites from "./pages/Favorites";
import Settings from "./pages/Settings";
import Login from "./pages/Login";
import Services from "./pages/Services";
import PriceEstimator from "./pages/PriceEstimator";
import CostCalculator from "./pages/CostCalculator";
import Consulenze from "./pages/Consulenze";
import PianoAcquisizione from "./pages/PianoAcquisizione";
import LetteraIntenti from "./pages/LetteraIntenti";
import SpazioPubblicitario from "./pages/SpazioPubblicitario";
import Admin from "./pages/Admin";
import Blog from "./pages/Blog";
import BlogArticle from "./pages/BlogArticle";
import SEOPage from "./pages/SEOPage";
import AuthCallback from "./pages/AuthCallback"; // kept for back-compat old email/magic links
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Claim from "./pages/Claim";
import VerifyEmail from "./pages/VerifyEmail";
import ResetPassword from "./pages/ResetPassword";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import CookieBanner from "./components/CookieBanner";
import SupportWidget from "./components/SupportWidget";
import { PwaInstallBanner } from "./components/PwaInstall";
import { AviationBackground } from "./components/AviationBackground";
import { useState, useEffect } from "react";

function AppContent() {
  const location = useLocation();

  // Scroll to top on every route change (unless navigating to a hash anchor)
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [location.pathname, location.hash]);

  // Light content-protection: disable right-click, text selection on images,
  // drag-save of images and common dev-tool shortcuts (Ctrl+S / Ctrl+U / F12 / Ctrl+Shift+I/J/C).
  // This is not bullet-proof (devtools can still be opened via menu) but discourages
  // casual scraping of photos and data from listings. Admin panel is exempt.
  useEffect(() => {
    const isAdmin = location.pathname.startsWith('/admin');
    if (isAdmin) return;

    // NEVER block form elements — on iOS PWA standalone a global preventDefault on
    // contextmenu can interfere with focus/keyboard opening on inputs.
    const isFormEl = (el) => {
      if (!el || !el.tagName) return false;
      const tag = el.tagName.toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'BUTTON' || tag === 'LABEL' || tag === 'OPTION') return true;
      if (el.isContentEditable) return true;
      // Climb up to a few ancestors to catch labels/wrappers
      let p = el.parentElement;
      for (let i = 0; p && i < 3; i++) {
        const pt = (p.tagName || '').toUpperCase();
        if (pt === 'INPUT' || pt === 'TEXTAREA' || pt === 'SELECT' || pt === 'LABEL') return true;
        p = p.parentElement;
      }
      return false;
    };

    const blockContext = (e) => {
      if (isFormEl(e.target)) return; // let iOS handle focus/selection on form fields
      e.preventDefault();
      return false;
    };
    const blockDragImg = (e) => {
      if (e.target && e.target.tagName === 'IMG') { e.preventDefault(); return false; }
    };
    const blockKeys = (e) => {
      if (!e || typeof e.key !== 'string') return;
      // F12
      if (e.key === 'F12' || e.keyCode === 123) { e.preventDefault(); return false; }
      // Ctrl+S, Ctrl+U, Ctrl+P
      if ((e.ctrlKey || e.metaKey) && ['s', 'u', 'p'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        return false;
      }
      // Ctrl+Shift+I / J / C (devtools)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && ['i', 'j', 'c'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('contextmenu', blockContext);
    document.addEventListener('dragstart', blockDragImg);
    document.addEventListener('keydown', blockKeys);
    return () => {
      document.removeEventListener('contextmenu', blockContext);
      document.removeEventListener('dragstart', blockDragImg);
      document.removeEventListener('keydown', blockKeys);
    };
  }, [location.pathname]);

  // Admin panel: standalone layout (no header/footer/background)
  if (location.pathname === '/admin' || location.pathname.startsWith('/admin/')) {
    return <Admin />;
  }

  return (
    <>
      <AviationBackground />
      <Header />
      <main className="min-h-screen">
        <Routes>
          <Route path="/" element={<Home />} />
          {/* SEO landing pages — category (buy & sell) */}
          <Route path="/comprare-ultraleggeri-usati" element={<CategoryLanding mode="buy" />} />
          <Route path="/comprare-aerei-pistoni-monomotore" element={<CategoryLanding mode="buy" />} />
          <Route path="/comprare-aerei-pistoni-bimotore" element={<CategoryLanding mode="buy" />} />
          <Route path="/comprare-aerei-turboelica" element={<CategoryLanding mode="buy" />} />
          <Route path="/comprare-jet-privati" element={<CategoryLanding mode="buy" />} />
          <Route path="/comprare-elicotteri" element={<CategoryLanding mode="buy" />} />
          <Route path="/vendere-ultraleggero" element={<CategoryLanding mode="sell" />} />
          <Route path="/vendere-aereo-pistoni-monomotore" element={<CategoryLanding mode="sell" />} />
          <Route path="/vendere-aereo-pistoni-bimotore" element={<CategoryLanding mode="sell" />} />
          <Route path="/vendere-aereo-turboelica" element={<CategoryLanding mode="sell" />} />
          <Route path="/vendere-jet-privato" element={<CategoryLanding mode="sell" />} />
          <Route path="/vendere-elicottero" element={<CategoryLanding mode="sell" />} />
          <Route path="/listings" element={<Listings />} />
          <Route path="/listings/:id" element={<ListingDetail />} />
          <Route path="/listings/:category/:slug" element={<ListingDetail />} />
          <Route path="/create-listing" element={<CreateListing />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/favorites" element={<Favorites />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/login" element={<Login />} />
          <Route path="/services" element={<Services />} />
          <Route path="/servizi" element={<Services />} />
          <Route path="/services/price-estimator" element={<PriceEstimator />} />
          <Route path="/services/cost-calculator" element={<CostCalculator />} />
          <Route path="/consulenze-compravendita-velivoli" element={<Consulenze />} />
          <Route path="/consulenze-compravendita-velivoli/" element={<Consulenze />} />
          <Route path="/piano-di-acquisizione-velivolo" element={<PianoAcquisizione />} />
          <Route path="/piano-di-acquisizione-velivolo/" element={<PianoAcquisizione />} />
          <Route path="/lettera-di-intenti-compravendita-velivolo-tra-privati" element={<LetteraIntenti />} />
          <Route path="/lettera-di-intenti-compravendita-velivolo-tra-privati/" element={<LetteraIntenti />} />
          <Route path="/spazio-pubblicitario" element={<SpazioPubblicitario />} />
          <Route path="/spazio-pubblicitario/" element={<SpazioPubblicitario />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/claim/:token" element={<Claim />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          {/* Chrono SEO: dynamic top-level SEO pages (catch-all, must be LAST) */}
          <Route path="/:slug" element={<SEOPage />} />
        </Routes>
      </main>
      <Footer />
      <SupportWidget />
      <PwaInstallBanner />
      <CookieBanner />
    </>
  );
}

function App() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const splash = document.getElementById('splash-screen');
    if (!splash) { setReady(true); return; }

    const fadeTimer = setTimeout(() => {
      splash.style.opacity = '0';
    }, 2400);

    const removeTimer = setTimeout(() => {
      if (splash.parentNode) splash.parentNode.removeChild(splash);
      setReady(true);
    }, 3100);

    return () => { clearTimeout(fadeTimer); clearTimeout(removeTimer); };
  }, []);

  if (!ready) return null;

  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
